import { useCallback, useEffect, useState } from 'react';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Callout, Field, Select } from '@beyond-js/ui/react';
import { Panel, Problem, useUnread } from '@accounts/ui';

type Work = 'off' | 'immediate' | 'digest';

/**
 * What the products may email the person about their work (decision E2): nothing, each notice, or one
 * message a day. Account mail — security, sign-in, invitations — is always sent, and says so. When
 * Accounts does not answer the choice, the page's notice says so once (D163) and the region in its place.
 */
export function Mail() {
	const { text, client } = usePresence();
	const [work, setWork] = useState<Work>();
	// Why the choice could not be read; a failed change is `failure`
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [saved, setSaved] = useState(false);

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(() => Refusal.answered(async () => setWork((await client.get<{ work: Work }>('/v1/account/mail')).work), setUnread), [client]);
	const outage = useUnread({ key: 'mail', title: text('mail.title'), unread, run: read });
	useEffect(() => {
		void read();
	}, [read]);

	async function choose(value: Work) {
		const before = work;
		setWork(value);
		setSaved(false);
		setFailure(undefined);
		try {
			await client.patch('/v1/account/mail', { work: value });
			setSaved(true);
		} catch (error) {
			setWork(before);
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			// Nothing came back, so the choice may have been saved: it is read again
			if (Refusal.is(error) && error.unknown) await read();
		}
	}

	return (
		// The choice waits for the account's answer rather than showing one it may not have (LOAD-01)
		<Panel id="mail" title={text('mail.title')} waiting={work === undefined && !unread ? { kind: 'lines', rows: 1 } : null} unavailable={outage && work === undefined}>
			{failure ? <Problem error={failure} check={read} /> : null}
			{unread && !outage ? <Problem error={unread} /> : null}
			{saved ? <Callout tone="success" live title={text('mail.saved')} /> : null}
			<Field label={text('mail.work')} hint={text('mail.account')}>
				<Select
					value={work ?? 'off'}
					disabled={!work}
					onChange={event => void choose(event.target.value as Work)}
					options={(['off', 'immediate', 'digest'] as Work[]).map(value => ({ value, label: text(`mail.work.${value}`) }))}
				/>
			</Field>
		</Panel>
	);
}
